import { useState, useEffect } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { base44 } from "../api/base44Client";
import {
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Grid,
  Alert,
  AlertTitle,
  Chip,
  AppBar,
  Toolbar,
  Container,
} from "@mui/material";
import SummaryView from "../components/financing/SummaryView";
import CustomerLinkManager from "../components/financing/CustomerLinkManager";
import {
  ArrowLeft,
  Download,
  Edit,
  Shield,
  Clock,
  User,
  FileText,
  AlertTriangle,
} from "lucide-react";
import moment from "moment";

export default function PraticaDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      if (id) {
        const records = await base44.entities.FinancingRequest.filter({ id });
        if (records.length > 0) setData(records[0]);
      }
      setLoading(false);
    }
    load();
  }, [id]);

  const handleDownloadJSON = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pratica-${data.pratica_id || data.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          p: 4,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography color="text.secondary">Pratica non trovata</Typography>
          <Button
            component={RouterLink}
            to="/admin"
            variant="text"
            sx={{ mt: 2 }}
          >
            Torna alla lista
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={1}
        sx={{ bgcolor: "background.paper" }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{ py: 1, display: "flex", justifyContent: "space-between" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton component={RouterLink} to="/admin" edge="start">
                <ArrowLeft size={20} />
              </IconButton>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Shield size={18} color="white" />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Pratica {data.pratica_id}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1 }}
                >
                  Dettaglio richiesta
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleDownloadJSON}
                startIcon={<Download size={14} />}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                JSON
              </Button>
              {data.stato_pratica !== "inviata" && (
                <Button
                  component={RouterLink}
                  to={`/?record_id=${data.id}`}
                  variant="contained"
                  size="small"
                  startIcon={<Edit size={14} />}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Modifica
                </Button>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <User size={20} color="white" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Richiedente
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {data.cognome && data.nome
                    ? `${data.cognome} ${data.nome}`
                    : "—"}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Clock size={20} color="white" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Ultima modifica
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {data.updated_date
                    ? moment(data.updated_date).format("DD/MM/YY HH:mm")
                    : "—"}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FileText size={20} color="white" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Completamento
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {data.completion_percentage || 0}%
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {data.campi_mancanti?.length > 0 && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{ mb: 4, borderRadius: 3 }}
          >
            <AlertTitle
              sx={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <AlertTriangle size={16} /> Campi mancanti (
              {data.campi_mancanti.length})
            </AlertTitle>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {data.campi_mancanti.map((f) => (
                <Chip
                  key={f}
                  label={f}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.625rem", height: 20 }}
                />
              ))}
            </Box>
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} lg={8}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 4 }}>
              <SummaryView data={data} />
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 4 }}>
              <CustomerLinkManager
                financingRequestId={data.id}
                praticaId={data.pratica_id}
                customerEmail={data.email}
                customerName={
                  data.nome && data.cognome
                    ? `${data.nome} ${data.cognome}`
                    : ""
                }
              />
            </Paper>
          </Grid>
        </Grid>

        {(data.log_importati?.length > 0 ||
          data.log_modificati?.length > 0) && (
          <Grid container spacing={2}>
            {data.log_importati?.length > 0 && (
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      display: "block",
                      mb: 1,
                      textTransform: "uppercase",
                    }}
                  >
                    Campi importati dalla piattaforma
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {data.log_importati.map((f) => (
                      <Chip
                        key={f}
                        label={f}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ fontSize: "0.625rem", height: 18 }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}
            {data.log_modificati?.length > 0 && (
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      display: "block",
                      mb: 1,
                      textTransform: "uppercase",
                    }}
                  >
                    Campi modificati dal cliente
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {data.log_modificati.map((f) => (
                      <Chip
                        key={f}
                        label={f}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ fontSize: "0.625rem", height: 18 }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
